import { collection, doc, getDoc, getDocs, orderBy, query, where } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { err, ok, type Result } from '@shared/result';
import { asId, type AreaId, type PersonId } from '@shared/brand';
import type {
  IDataConfirmationRepository,
  RepositoryError,
  SubmitDataConfirmationInput,
} from '@application/ports';
import type { DataConfirmation, DataCorrection } from '@domain/entities/DataConfirmation';
import { isConfirmableField } from '@domain/entities/DataConfirmation';
import { COLLECTIONS, firebase } from '../firebase/client';
import { translateCallableError, translateFirestoreError } from './support';

/**
 * La confirmación de datos, contra Firestore.
 *
 * **Un documento ausente no es un error**, igual que en el expediente: significa
 * que esa persona todavía no ha entrado a contestar, que es el estado normal de
 * casi todo el mundo el primer día. Devolver `null` lo dice; un `not-found`
 * obligaría a cada llamador a distinguir el hueco esperado del fallo real, y esa
 * distinción se pierde en cuanto alguien escribe el primer `catch`.
 */
export class FirestoreDataConfirmationRepository implements IDataConfirmationRepository {
  async getFor(personId: PersonId): Promise<Result<DataConfirmation | null, RepositoryError>> {
    try {
      const { db } = firebase();
      const snapshot = await getDoc(doc(db, COLLECTIONS.dataConfirmations, personId));
      if (!snapshot.exists()) return ok(null);
      return ok(decodeDataConfirmation(snapshot.id, snapshot.data()));
    } catch (cause) {
      return err(translateFirestoreError(cause, `${COLLECTIONS.dataConfirmations}/${personId}`));
    }
  }

  /**
   * La bandeja.
   *
   * **La consulta filtra por `areaId` porque la regla lo exige**, no por comodidad.
   * `allow list` compara `resource.data.areaId` contra el alcance de quien mira, y
   * una consulta que no nombre ese campo la incumple entera: falla sólo para quien
   * no es superusuario, que es la forma en que este proyecto ya se ha equivocado
   * antes y la razón de que esté escrito aquí.
   */
  async listReported(
    areaId: AreaId | null,
  ): Promise<Result<readonly DataConfirmation[], RepositoryError>> {
    try {
      const { db } = firebase();
      const base = collection(db, COLLECTIONS.dataConfirmations);
      const snapshot = await getDocs(
        areaId === null
          ? query(base, where('reported', '==', true), orderBy('confirmedAt', 'desc'))
          : query(
              base,
              where('areaId', '==', areaId),
              where('reported', '==', true),
              orderBy('confirmedAt', 'desc'),
            ),
      );
      return ok(snapshot.docs.map((item) => decodeDataConfirmation(item.id, item.data())));
    } catch (cause) {
      return err(translateFirestoreError(cause, COLLECTIONS.dataConfirmations));
    }
  }

  async submit(input: SubmitDataConfirmationInput): Promise<Result<void, RepositoryError>> {
    try {
      const { functions } = firebase();
      const invoke = httpsCallable<SubmitDataConfirmationInput, void>(
        functions,
        'confirmMyData',
      );
      await invoke(input);
      return ok(undefined);
    } catch (cause) {
      return err(translateCallableError(cause, 'confirmMyData'));
    }
  }

  async resolve(personId: PersonId): Promise<Result<void, RepositoryError>> {
    try {
      const { functions } = firebase();
      const invoke = httpsCallable<{ personId: string }, void>(functions, 'resolveDataReport');
      await invoke({ personId });
      return ok(undefined);
    } catch (cause) {
      return err(translateCallableError(cause, 'resolveDataReport'));
    }
  }
}

/**
 * Lo que no se reconoce se descarta, y el documento sigue siendo legible.
 *
 * Una corrección con un campo que este build no conoce —porque se agregó al
 * catálogo después— se tira en vez de tumbar la bandeja entera: el resto de las
 * correcciones de esa persona sí se leen. Es la misma decisión que en el
 * expediente, y por el mismo motivo.
 */
export const decodeDataConfirmation = (
  id: string,
  data: Record<string, unknown>,
): DataConfirmation => {
  const texto = (value: unknown): string => (typeof value === 'string' ? value : '');
  const fecha = (value: unknown): Date | null => {
    if (value !== null && typeof value === 'object' && 'toDate' in value) {
      return (value as { toDate: () => Date }).toDate();
    }
    return null;
  };

  const raw = Array.isArray(data['corrections']) ? data['corrections'] : [];
  const corrections = raw
    .map((item): DataCorrection | null => {
      if (item === null || typeof item !== 'object') return null;
      const entry = item as Record<string, unknown>;
      if (!isConfirmableField(entry['field'])) return null;
      return {
        field: entry['field'],
        shown: texto(entry['shown']),
        correct: texto(entry['correct']),
      };
    })
    .filter((item): item is DataCorrection => item !== null);

  return {
    personId: asId<'PersonId'>(id),
    areaId: asId<'AreaId'>(texto(data['areaId'])),
    personName: texto(data['personName']),
    confirmedAt: fecha(data['confirmedAt']) ?? new Date(0),
    corrections,
    resolvedAt: fecha(data['resolvedAt']),
    resolvedByPersonId:
      typeof data['resolvedByPersonId'] === 'string'
        ? asId<'PersonId'>(data['resolvedByPersonId'])
        : null,
    /* Sin clave es la ronda 1: los 38 documentos de agosto se escribieron antes
       de que la versión existiera. Leerlos como la versión actual les daría por
       contestado un cuestionario que nunca vieron. */
    version: typeof data['version'] === 'number' ? data['version'] : 1,
  };
};
